// Opening hours per menu: its own configured hours and the effective hours
// through the main number (intersection along the shortest route).

import { useGuide } from './context';
import { nodeName, shortestPath } from './model';
import { Card, SectionHead, Badge, OpenBadge, HoursTable, HolidayList } from './components';
import { effectiveHours, compactHours } from './lib/callFlowStatus';
import { dayShort } from './i18n';

export function HoursSection({ selectedId }: { selectedId?: string }) {
  const { t, flow, model, lang, navigate } = useGuide();
  const menus = model.menus.filter(m => !model.afterHoursMenuIds.has(m.id));
  const shown = selectedId ? menus.filter(m => m.id === selectedId) : menus;
  return (
    <div className="space-y-3">
      <SectionHead title={t('hoursTitle')} intro={t('hoursIntro')} actions={
        selectedId ? <button type="button" onClick={() => navigate({ section: 'hours' })} className="text-xs text-purple-700 hover:underline">{t('all')}</button> : undefined
      } />
      {model.iana && <p className="text-[11px] text-gray-400">{t('timeZone', { tz: model.iana })}</p>}
      <div className="grid md:grid-cols-2 gap-3">
        {shown.map(aa => {
          const path = shortestPath(model, aa.id);
          const eff = effectiveHours(flow, path);
          const own = aa.id === model.rootId ? (aa.hours || flow.schedule.weekly) : aa.hours;
          const ownHolidays = aa.id === model.rootId ? (aa.holidays?.length ? aa.holidays : flow.schedule.holidayRanges) : aa.holidays;
          return (
            <Card key={aa.id} className="p-4">
              <div className="flex items-center gap-2 mb-1">
                {aa.language && <Badge kind="gray">{aa.language}</Badge>}
                <OpenBadge weekly={eff.weekly} holidays={eff.holidays} iana={eff.iana} />
              </div>
              <button type="button" onClick={() => navigate({ section: 'callflow', id: aa.id })} className="text-left">
                <h3 className="text-sm font-semibold text-gray-900 hover:underline">{nodeName(aa, lang)}</h3>
              </button>
              <div className="text-[11px] text-gray-500 mb-2">{aa.label}</div>
              <div className="text-xs font-medium text-gray-700 mb-1">{t('ownHours')}</div>
              {own ? <HoursTable weekly={own} compact /> : <div className="text-xs text-gray-500">{t('inherited')}</div>}
              <div className="mt-2 text-xs bg-gray-50 border border-gray-100 rounded px-2.5 py-1.5">
                <span className="font-medium text-gray-800">{t('effectiveHours')}: </span>
                <span className="text-gray-600">{eff.weekly ? compactHours(eff.weekly, dayShort(lang), t('closed')) : t('hoursUnknown')}</span>
              </div>
              {(ownHolidays?.length || 0) > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-medium text-gray-700 mb-1">{t('holidays')}</div>
                  <HolidayList holidays={ownHolidays} />
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
